import { Fragment, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import { DivIcon, LatLngBounds, LatLngExpression, Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SectionHeader from '../../components/SectionHeader';
import Spinner from '../../components/Spinner';
import Button from '../../components/Button';
import { useGatesWMinRoute } from '../../hooks/useGates';
import { deleteGates } from '../../api/gates';
import markerIconPng from '../../icons/marker-icon-green.png';
import endMarkerIconPng from '../../icons/marker-icon-red.png';
import { Gate } from '../../types/Gate';
import { HiOutlineChevronDown, HiOutlineChevronRight } from 'react-icons/hi2';

type GateSummary = {
  id: string;
  start: LatLngExpression;
  end: LatLngExpression | null;
  label: string;
  routeDescription: string;
  popupLabel: string;
  isValid: boolean;
  gate: Gate;
  order: number;
  cluster: number;
};

type ClusterGroup = {
  clusterId: number;
  clusterName: string;
  gates: GateSummary[];
};

const FALLBACK_CENTER: LatLngExpression = [51.5074, -0.1278]; // Default to London if no gates returned

export default function RouteManagement(): ReactElement {
  const { data: gates, isPending, isError, refetch } = useGatesWMinRoute();

  const createGateIcon = useCallback(
    (type: 'start' | 'end', label: string) =>
      new DivIcon({
        className: '',
        html: `
          <div style="position: relative; display: inline-flex; align-items: center; justify-content: center;">
            <img src="${type === 'start' ? markerIconPng : endMarkerIconPng}" alt="Gate marker" style="width: 25px; height: 41px; filter: drop-shadow(0 1px 2px rgba(0,0,0,0.25));" />
            <span style="position: absolute; top: -12px; left: 50%; transform:
             translateX(-50%); min-width: 20px; padding: 2px 6px; border-radius: 9999px; border: 2px solid #ffffff;
              background: ${
                type === 'start' ? '#0f766e' : '#b91c1c'
              }; color: #ffffff; font-size: 12px; font-weight: 700; line-height: 1; text-align: center;">${label}</span>
          </div>
        `,
        iconAnchor: [12, 41],
        popupAnchor: [0, -36],
        tooltipAnchor: [0, -36],
      }),
    [markerIconPng, endMarkerIconPng],
  );

  const [activeGateId, setActiveGateId] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState<'all' | 'selected'>('all');
  const [expandedClusters, setExpandedClusters] = useState<Set<number>>(new Set());
  const [filterMode, setFilterMode] = useState<'all' | 'merged' | 'unused'>('all');
  const [selectedGateIds, setSelectedGateIds] = useState<Set<string>>(new Set());

  const gateSummaries = useMemo<GateSummary[]>(() => {
    if (!gates) return [];

    const summaries = gates
      .map((gate: Gate, index: number) => {
        const startLat = gate.gates_lat?.[0];
        const startLon = gate.gates_lon?.[0];
        if (typeof startLat !== 'number' || typeof startLon !== 'number') {
          return null;
        }

        const endLat = gate.gates_lat?.[1];
        const endLon = gate.gates_lon?.[1];
        const end =
          typeof endLat === 'number' && typeof endLon === 'number' ? ([endLat, endLon] as LatLngExpression) : null;

        const labelCandidate =
          (gate.address && gate.address.trim()) ||
          (gate.description && gate.description.trim()) ||
          (gate.notes && gate.notes.trim()) ||
          (gate.route_id && gate.route_id.trim());
        const label = labelCandidate && labelCandidate.length > 0 ? labelCandidate : gate.id;
        const routeDescription = gate.route_description?.trim() || '';
        const popupLabel = (gate.notes && gate.notes.trim()) || routeDescription || label;

        return {
          id: gate.id,
          start: [startLat, startLon] as LatLngExpression,
          end,
          label,
          routeDescription,
          popupLabel,
          isValid: gate.valid,
          gate,
          order: index + 1,
          cluster: gate.cluster ?? 0,
        };
      })
      .filter(Boolean) as GateSummary[];

    return summaries;
  }, [gates]);

  const filteredGateSummaries = useMemo(() => {
    switch (filterMode) {
      case 'unused':
        return gateSummaries.filter((gate) => !gate.gate.sessions || gate.gate.sessions.length === 0);
      case 'merged':
        return gateSummaries.filter((gate) => gate.gate.creation_mode === '3');
      case 'all':
      default:
        return gateSummaries;
    }
  }, [gateSummaries, filterMode]);

  const clusteredGates = useMemo<ClusterGroup[]>(() => {
    if (!filteredGateSummaries.length) return [];

    // Group gates by cluster
    const groups = filteredGateSummaries.reduce(
      (acc, gate) => {
        const clusterId = gate.cluster;
        if (!acc[clusterId]) {
          acc[clusterId] = [];
        }
        acc[clusterId].push(gate);
        return acc;
      },
      {} as Record<number, GateSummary[]>,
    );

    // Convert to array and sort by cluster ID
    return Object.entries(groups)
      .map(([clusterId, gatesInCluster]) => {
        // Find the most common address in the cluster
        const addressCounts: Record<string, number> = {};

        gatesInCluster.forEach((gate) => {
          const address = gate.gate.address?.trim();
          if (address) {
            addressCounts[address] = (addressCounts[address] || 0) + 1;
          }
        });

        // Get the most frequent address (modal address)
        let modalAddress = '';
        let maxCount = 0;

        Object.entries(addressCounts).forEach(([address, count]) => {
          if (count > maxCount) {
            maxCount = count;
            modalAddress = address;
          }
        });

        // If no address found, fallback to description or notes
        if (!modalAddress) {
          modalAddress =
            gatesInCluster.find((g) => g.gate.address)?.gate.address ||
            gatesInCluster.find((g) => g.gate.description)?.gate.description ||
            'Unknown Location';
        }

        // Create range display (start - end) - Address
        const gateNumbers = gatesInCluster.map((gate) => gate.order).sort((a, b) => a - b);
        const minGate = Math.min(...gateNumbers);
        const maxGate = Math.max(...gateNumbers);
        const rangeDisplay = minGate === maxGate ? `(${minGate})` : `(${minGate} - ${maxGate})`;
        const clusterName = `${rangeDisplay} - ${modalAddress}`;

        return {
          clusterId: parseInt(clusterId),
          clusterName,
          gates: gatesInCluster.sort((a, b) => a.order - b.order),
        };
      })
      .sort((a, b) => a.clusterId - b.clusterId);
  }, [filteredGateSummaries]);

  const selectedGate = useMemo(
    () => gateSummaries.find((gate) => gate.id === activeGateId) ?? null,
    [gateSummaries, activeGateId],
  );

  const allBounds = useMemo(() => {
    if (!gateSummaries.length) return null;
    const positions: LatLngExpression[] = [];
    gateSummaries.forEach((gate) => {
      positions.push(gate.start);
      if (gate.end) positions.push(gate.end);
    });
    return positions.length ? new LatLngBounds(positions) : null;
  }, [gateSummaries]);

  const selectedBounds = useMemo(() => {
    if (!selectedGate) return null;
    const points: LatLngExpression[] = [selectedGate.start];
    if (selectedGate.end) points.push(selectedGate.end);
    return new LatLngBounds(points);
  }, [selectedGate]);

  const bounds = focusMode === 'selected' && selectedBounds ? selectedBounds : allBounds;
  const mapCenter = selectedGate?.start || gateSummaries[0]?.start || FALLBACK_CENTER;

  const [showBothGates, setShowBothGates] = useState(false);
  const [fitBoundsTrigger, setFitBoundsTrigger] = useState(0);

  const handleMapBoundsChange = useCallback((shouldShow: boolean) => {
    setShowBothGates((prev) => (prev === shouldShow ? prev : shouldShow));
  }, []);

  const handleFocusGate = useCallback((gateId: string) => {
    setActiveGateId(gateId);
    setFocusMode('selected');
    setFitBoundsTrigger((prev) => prev + 1);
  }, []);

  const handleZoomAll = useCallback(() => {
    setFocusMode('all');
    setFitBoundsTrigger((prev) => prev + 1);
  }, []);

  const toggleCluster = useCallback((clusterId: number) => {
    setExpandedClusters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clusterId)) {
        newSet.delete(clusterId);
      } else {
        newSet.add(clusterId);
      }
      return newSet;
    });
  }, []);

  const toggleGateSelection = useCallback((gateId: string) => {
    setSelectedGateIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(gateId)) {
        newSet.delete(gateId);
      } else {
        newSet.add(gateId);
      }
      return newSet;
    });
  }, []);

  const toggleClusterSelection = useCallback((cluster: ClusterGroup) => {
    setSelectedGateIds((prev) => {
      const newSet = new Set(prev);
      const clusterGateIds = cluster.gates.map((g) => g.id);
      const allSelected = clusterGateIds.every((id) => prev.has(id));

      if (allSelected) {
        // If all are selected, deselect all in cluster
        clusterGateIds.forEach((id) => newSet.delete(id));
      } else {
        // Otherwise, select all in cluster
        clusterGateIds.forEach((id) => newSet.add(id));
      }

      return newSet;
    });
  }, []);

  const handleDeleteSelected = useCallback(async () => {
    const idsToDelete = Array.from(selectedGateIds);
    const confirmed = window.confirm(
      `Are you sure you want to delete ${idsToDelete.length} gate${idsToDelete.length > 1 ? 's' : ''}?`,
    );

    if (!confirmed) return;

    try {
      await deleteGates(idsToDelete);
      // Clear selection after successful delete
      setSelectedGateIds(new Set());
      // Refetch gates to update the list
      refetch();
    } catch (error) {
      console.error('Failed to delete gates:', error);
      alert('Failed to delete gates. Please try again.');
    }
  }, [selectedGateIds, refetch]);

  return (
    <div className="cr-w-full">
      <SectionHeader title="Route Management" isLoading={isPending} />

      <div className="cr-mt-6 cr-grid cr-gap-6">
        <div className="cr-relative cr-h-[500px] cr-overflow-hidden cr-rounded-xl cr-bg-white cr-shadow-sm">
          {isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {isError && !isPending && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center cr-text-red-600">
              Unable to load gates for this team. Please try again later.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length === 0 && (
            <div className="cr-flex cr-h-full cr-items-center cr-justify-center">
              No gates available for this team yet.
            </div>
          )}

          {!isError && !isPending && gateSummaries.length > 0 && (
            <>
              {selectedGate && gateSummaries.length > 1 && (
                <div className="cr-absolute cr-right-4 cr-top-4 cr-z-[1000]">
                  <button
                    type="button"
                    onClick={handleZoomAll}
                    className="cr-rounded-md cr-bg-white cr-px-3 cr-py-1 cr-text-xs cr-font-semibold
                     cr-text-[#03365D] cr-shadow-sm hover:cr-bg-cirrusBlue-25"
                  >
                    All Gates
                  </button>
                </div>
              )}
              <MapContainer center={mapCenter} zoom={13} scrollWheelZoom className="cr-h-full cr-w-full">
                <FitBounds bounds={bounds} trigger={fitBoundsTrigger} />
                <BoundsListener onBoundsChange={handleMapBoundsChange} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {gateSummaries.map((marker) => (
                  <Fragment key={marker.id}>
                    <Marker position={marker.start} icon={createGateIcon('start', String(marker.order))}>
                      <Popup>
                        <div className="cr-min-w-[180px]">
                          <p className="cr-font-semibold">{marker.popupLabel}</p>
                          <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                          <p className="cr-text-xs cr-text-gray-500">Start gate</p>
                          <p className="cr-text-sm cr-text-gray-600">
                            {marker.isValid ? 'Valid' : 'Not yet validated'}
                          </p>
                          {marker.gate.sessions && marker.gate.sessions.length > 0 && (
                            <div className="cr-mt-2 cr-border-t cr-pt-2">
                              <p className="cr-text-xs cr-font-semibold">Sessions:</p>
                              {marker.gate.sessions.slice(0, 3).map((session, idx) => (
                                <p key={idx} className="cr-text-xs cr-text-gray-600">
                                  {new Date(session.date).toLocaleDateString()} - {session.description}
                                </p>
                              ))}
                              {marker.gate.sessions.length > 3 && (
                                <p className="cr-text-xs cr-italic cr-text-gray-500">
                                  ...and {marker.gate.sessions.length - 3} more
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                    {showBothGates && marker.end && (
                      <Marker position={marker.end} icon={createGateIcon('end', String(marker.order))}>
                        <Popup>
                          <div className="cr-min-w-[180px]">
                            <p className="cr-font-semibold">{marker.popupLabel}</p>
                            <p className="cr-text-xs cr-text-gray-500">Gate #{marker.order}</p>
                            <p className="cr-text-xs cr-text-gray-500">End gate</p>
                            <p className="cr-text-sm cr-text-gray-600">
                              {marker.isValid ? 'Valid' : 'Not yet validated'}
                            </p>
                            {marker.gate.sessions && marker.gate.sessions.length > 0 && (
                              <div className="cr-mt-2 cr-border-t cr-pt-2">
                                <p className="cr-text-xs cr-font-semibold">Sessions:</p>
                                {marker.gate.sessions.slice(0, 3).map((session, idx) => (
                                  <p key={idx} className="cr-text-xs cr-text-gray-600">
                                    {new Date(session.date).toLocaleDateString()} - {session.description}
                                  </p>
                                ))}
                                {marker.gate.sessions.length > 3 && (
                                  <p className="cr-text-xs cr-italic cr-text-gray-500">
                                    ...and {marker.gate.sessions.length - 3} more
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </Fragment>
                ))}
              </MapContainer>
            </>
          )}
        </div>

        <div className="cr-rounded-xl cr-bg-white cr-p-6 cr-shadow-sm">
          <div className="cr-mb-4 cr-flex cr-items-center cr-justify-between">
            <div className="cr-flex cr-gap-6">
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                <input
                  type="radio"
                  name="gateFilter"
                  value="all"
                  checked={filterMode === 'all'}
                  onChange={() => {
                    setFilterMode('all');
                    setSelectedGateIds(new Set());
                  }}
                  className="cr-h-4 cr-w-4 cr-border-gray-300 cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100"
                />
                <span className="cr-text-sm cr-font-medium">All</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                <input
                  type="radio"
                  name="gateFilter"
                  value="merged"
                  checked={filterMode === 'merged'}
                  onChange={() => {
                    setFilterMode('merged');
                    setSelectedGateIds(new Set());
                  }}
                  className="cr-h-4 cr-w-4 cr-border-gray-300 cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100"
                />
                <span className="cr-text-sm cr-font-medium">Merged</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                <input
                  type="radio"
                  name="gateFilter"
                  value="unused"
                  checked={filterMode === 'unused'}
                  onChange={() => {
                    setFilterMode('unused');
                    setSelectedGateIds(new Set());
                  }}
                  className="cr-h-4 cr-w-4 cr-border-gray-300 cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100"
                />
                <span className="cr-text-sm cr-font-medium">Unused</span>
              </label>
            </div>
            {filterMode === 'unused' && selectedGateIds.size > 0 && (
              <Button onClick={handleDeleteSelected} className="cr-bg-red-600 cr-text-white hover:cr-bg-red-700">
                DELETE ({selectedGateIds.size})
              </Button>
            )}
          </div>

          <div className="cr-flex cr-flex-col cr-gap-2">
            {clusteredGates.map((cluster) => {
              const isExpanded = expandedClusters.has(cluster.clusterId);
              const clusterGateIds = cluster.gates.map((g) => g.id);
              const selectedInCluster = clusterGateIds.filter((id) => selectedGateIds.has(id));
              const allInClusterSelected =
                selectedInCluster.length === clusterGateIds.length && clusterGateIds.length > 0;
              const someInClusterSelected =
                selectedInCluster.length > 0 && selectedInCluster.length < clusterGateIds.length;

              return (
                <div key={cluster.clusterId} className="cr-overflow-hidden cr-rounded-lg cr-border cr-border-gray-200">
                  {/* Cluster Header */}
                  <div className="cr-flex cr-items-center cr-bg-gray-50 cr-transition-colors hover:cr-bg-gray-100">
                    <button
                      onClick={() => toggleCluster(cluster.clusterId)}
                      className="cr-flex cr-flex-1 cr-items-center cr-justify-between cr-p-4"
                    >
                      <div className="cr-flex cr-items-center cr-gap-2">
                        {isExpanded ? (
                          <HiOutlineChevronDown className="cr-h-5 cr-w-5 cr-text-gray-500" />
                        ) : (
                          <HiOutlineChevronRight className="cr-h-5 cr-w-5 cr-text-gray-500" />
                        )}
                        <span className="cr-text-2xl cr-font-semibold">{cluster.clusterName}</span>
                        <span className="cr-text-lg cr-text-gray-500">({cluster.gates.length} gates)</span>
                        {selectedInCluster.length > 0 && (
                          <span className="cr-text-sm cr-font-medium cr-text-cirrusBlue-100">
                            ({selectedInCluster.length} selected)
                          </span>
                        )}
                      </div>
                    </button>
                    <div className="cr-px-4">
                      <label className="cr-flex cr-cursor-pointer cr-items-center cr-gap-2">
                        <input
                          type="checkbox"
                          checked={allInClusterSelected}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate = someInClusterSelected;
                            }
                          }}
                          onChange={() => toggleClusterSelection(cluster)}
                          className="cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100"
                        />
                        <span className="cr-text-sm cr-text-gray-600">Select all</span>
                      </label>
                    </div>
                  </div>

                  {/* Expandable Gate List */}
                  {isExpanded && (
                    <div className="cr-border-t cr-border-gray-200">
                      {cluster.gates.map((marker) => {
                        const isActive = marker.id === activeGateId;
                        return (
                          <div
                            key={marker.id}
                            onClick={() => handleFocusGate(marker.id)}
                            className={`cr-flex cr-cursor-pointer cr-items-center cr-justify-between
                            cr-border-b cr-border-gray-100 cr-p-4 cr-transition-colors last:cr-border-b-0
                            hover:cr-bg-gray-50 ${isActive ? 'cr-bg-cirrusBlue-25' : ''}`}
                          >
                            <div className="cr-flex cr-items-start cr-gap-3 cr-pl-7">
                              <input
                                type="checkbox"
                                checked={selectedGateIds.has(marker.id)}
                                onChange={() => toggleGateSelection(marker.id)}
                                onClick={(e) => e.stopPropagation()}
                                className="cr-mt-1 cr-h-4 cr-w-4 cr-rounded cr-border-gray-300 cr-text-cirrusBlue-100 focus:cr-ring-cirrusBlue-100"
                              />
                              <span className="cr-flex cr-h-6 cr-w-6 cr-items-center cr-justify-center cr-rounded-full cr-bg-cirrusBlue-100 cr-text-xs cr-font-semibold cr-text-white">
                                {marker.order}
                              </span>
                              <div>
                                <p className="cr-font-medium">{marker.label}</p>
                                {marker.routeDescription && (
                                  <p className="cr-text-xs cr-text-gray-500">{marker.routeDescription}</p>
                                )}
                                <p className="cr-text-xs cr-text-gray-500">
                                  Distance: {Math.round(marker.gate.route_distance)}m | Bearing:{' '}
                                  {marker.gate.route_bearing < 0
                                    ? Math.round(marker.gate.route_bearing + 360)
                                    : Math.round(marker.gate.route_bearing)}
                                  ° | Elevation: {marker.gate.elevation_difference.toFixed(2)}m
                                </p>
                                <p className="cr-text-xs cr-text-gray-500">
                                  Status: {marker.isValid ? 'Valid' : 'Not validated'}
                                </p>
                                {marker.gate.sessions && marker.gate.sessions.length > 0 && (
                                  <div className="cr-mt-2">
                                    <p className="cr-text-xs cr-font-semibold cr-text-gray-700">Sessions:</p>
                                    <div className="cr-pl-2">
                                      {marker.gate.sessions.map((session, idx) => (
                                        <p key={idx} className="cr-text-xs cr-text-gray-600">
                                          {new Date(session.date).toLocaleDateString()} - {session.description}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                <p className="cr-text-xs cr-text-gray-500">Gate ID: {marker.id}</p>
                              </div>
                            </div>
                            <div className="cr-flex cr-items-center cr-gap-2">
                              <Button
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleFocusGate(marker.id);
                                }}
                              >
                                Center
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function FitBounds({ bounds, trigger }: { bounds: LatLngBounds | null; trigger: number }): ReactElement | null {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [map, bounds, trigger]);

  return null;
}

function BoundsListener({ onBoundsChange }: { onBoundsChange: (shouldShow: boolean) => void }): ReactElement | null {
  const map = useMapEvents({
    zoomend: () => handleBoundsChange(map, onBoundsChange),
    moveend: () => handleBoundsChange(map, onBoundsChange),
  });

  useEffect(() => {
    handleBoundsChange(map, onBoundsChange);
  }, [map, onBoundsChange]);

  return null;
}

function handleBoundsChange(map: LeafletMap, onBoundsChange: (shouldShow: boolean) => void): void {
  const bounds = map.getBounds();
  if (!bounds.isValid()) return;
  const diagonalDistanceMeters = bounds.getNorthEast().distanceTo(bounds.getSouthWest());
  onBoundsChange(diagonalDistanceMeters <= 10_000);
}
